// Page - Step 2: Content Selection
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Heart, Star, MessageCircle, Video, Image, ArrowRight, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

// Helper for large numbers
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num > 10000 ? (num / 10000).toFixed(1) + 'w' : num;
};

// Helper to fix HEIF URLs for browser compatibility
const getCompatibleCoverUrl = (url) => {
  if (!url) return null;
  // Replace format/heif with format/webp for better compatibility
  // Also handle urlencoded params just in case
  let newUrl = url.replace('/format/heif', '/format/webp').replace('/format/heic', '/format/webp');
  if (!newUrl.includes('format/')) {
      // If no format specified, try appending or leaving as is? 
      // XHS usually defaults to webp or jpg if not specified, but let's be safe.
  }
  return newUrl;
};

const ContentCard = ({ item, isSelected, onToggle, onViewDetail, taskStatus }) => {
  // Safe tag parsing
  const tagsList = item.tags 
    ? (typeof item.tags === 'string' ? item.tags.split(',') : (Array.isArray(item.tags) ? item.tags : []))
    : [];

  const handleDetail = (e) => {
    e.stopPropagation();
    onViewDetail(item);
  };

  return (
    <div 
      className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 group cursor-pointer relative flex flex-col ${isSelected ? 'border-[#256df3] ring-2 ring-[#256df3]/20 shadow-lg translate-y-[-2px]' : 'border-gray-100 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.05)] hover:shadow-md hover:border-gray-200'}`}
      onClick={() => onToggle(item.id)}
      style={{ minHeight: '420px', height: 'auto' }}
    >
      {/* Header Image/Video */}
      <div className="relative h-48 bg-gray-100 overflow-hidden flex-shrink-0 group-hover:scale-[1.02] transition-transform duration-500">
        {item.coverUrl ? (
          <img 
            src={getCompatibleCoverUrl(item.coverUrl)} 
            alt={item.title} 
            className="w-full h-full object-cover" 
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.target.onerror = null; 
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex'; // Show fallback
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400">
            {item.type === 'video' ? <Video size={32} /> : <Image size={32} />}
          </div>
        )}
        {/* Fallback for broken images */}
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 hidden">
            <Image size={32} />
        </div>
        
        {/* Media Type Badge */}
        <div className="absolute top-3 right-3 flex flex-col gap-1 items-end z-10">
          <div className="px-2 py-1 bg-black/50 backdrop-blur-sm rounded-md text-white text-[10px] font-medium flex items-center space-x-1">
            {item.type === 'video' ? <Video size={12} /> : <Image size={12} />}
            <span>{item.type === 'video' ? '视频' : '图文'}</span>
          </div>
          {taskStatus === 'GENERATED' && (
            <div className={`px-2 py-1 backdrop-blur-sm rounded-md text-[10px] font-bold ${item.isSelected ? 'bg-green-500/90 text-white' : 'bg-gray-500/90 text-white'}`}>
              {item.isSelected ? '已使用' : '未使用'}
            </div>
          )}
        </div>
        
        {/* Selection Overlay */}
        <div className={`absolute inset-0 bg-black/10 transition-opacity flex items-start justify-start p-3 z-20 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
           <input 
              type="checkbox" 
              className="w-5 h-5 rounded border-gray-300 text-[#256df3] focus:ring-[#256df3]"
              checked={isSelected}
              onChange={() => {}} 
          />
        </div>

        {/* View Detail Button (Hover) - Higher Z-Index than Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 pointer-events-none">
            <button 
                onClick={handleDetail}
                className="bg-white/90 backdrop-blur text-gray-800 text-xs px-4 py-2 rounded-full font-medium shadow-lg hover:bg-white pointer-events-auto transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 hover:scale-105 active:scale-95"
            >
                查看详情
            </button>
        </div>
      </div>

      {/* Content Body */}
      <div className="p-4 flex flex-col flex-1 min-h-[180px] flex-shrink-0">
        {/* Author */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400 overflow-hidden border border-gray-200">
                {(item.author && item.author.trim() !== '' && item.author !== 'Unknown' && item.author !== '未知作者') ? item.author[0] : 'U'}
            </div>
            <span className="text-xs font-medium text-gray-600 truncate max-w-[120px]" title={item.author}>
                {item.author === 'Unknown' ? '未知作者' : item.author || '未知作者'}
            </span>
          </div>
          <span className="text-[10px] text-gray-400">{new Date(item.date).toLocaleDateString()}</span>
        </div>
        
        {/* Title & Snippet */}
        <h3 className="font-bold text-gray-800 text-sm mb-2 leading-snug group-hover:text-[#256df3] transition-colors line-clamp-2" title={item.title}>
            {item.title || '无标题'}
        </h3>
        <p className="text-xs text-gray-500 mb-3 leading-relaxed flex-1 line-clamp-3" title={item.snippet || item.content}>
            {item.snippet || item.content || item.title || '暂无描述内容...'}
        </p>
        
        {/* Stats Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1 text-gray-400" title="点赞">
              <Heart size={14} className={item.likes > 0 ? "text-red-500/70" : ""} />
              <span className="text-[11px] font-medium">{formatNumber(item.likes)}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-400" title="收藏">
              <Star size={14} className={item.favs > 0 ? "text-yellow-500/70" : ""} />
              <span className="text-[11px] font-medium">{formatNumber(item.favs)}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-400" title="评论">
              <MessageCircle size={14} />
              <span className="text-[11px] font-medium">{formatNumber(item.comments)}</span>
            </div>
          </div>
          
          {item.url && (
              <a 
                href={item.url} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[10px] text-blue-500 hover:underline flex items-center"
              >
                  原文 
                  <ArrowRight size={10} className="ml-0.5" />
              </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ContentSelection() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [task, setTask] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]); 
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDetailItem, setViewDetailItem] = useState(null); // Modal state

  // Fetch Real Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch task details
        const taskRes = await axios.get(`${API_URL}/tasks/${id}`, {
          headers: { 'x-user-email': user.email }
        });
        setTask(taskRes.data);

        // Fetch items
        const res = await axios.get(`${API_URL}/tasks/${id}/content`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log("Fetched Items:", res.data); // DEBUG
        setItems(res.data);
        
        // 建议2: 取消默认选中，全凭客户手工选择
        setSelectedItems([]);
      } catch (err) {
        console.error("Failed to load content", err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [id, user]);

  const toggleItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(i => i !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const toggleAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i.id));
    }
  };

  
  const [zhimoLoading, setZhimoLoading] = useState(false);
  const handleSendToZhimo = async () => {
    if (selectedItems.length === 0) {
      alert("请至少选择一篇内容");
      return;
    }
    try {
      setZhimoLoading(true);
      // Map selected items to the assets format NexUI expects
      const assets = data
        .filter(item => selectedItems.includes(item.id))
        .map(item => ({
          name: `[思序导入] ${item.title?.substring(0, 20) || '无标题素材'}`,
          scenarioType: 'geo_insight',
          configMeta: {
            source: 'nexgeo',
            geoAssetId: item.id,
            title: item.title,
            tags: item.tags,
            url: item.url,
            author: item.author
          }
        }));

      const res = await axios.post(`${API_URL}/zhimo/handover-to-ui`, { assets }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (res.data.success && res.data.redirectUrl) {
        // Redirect completely, or open in new tab
        window.open(res.data.redirectUrl, '_blank');
      } else {
        alert('发送到智墨失败: ' + (res.data.error || '未知错误'));
      }
    } catch (err) {
      console.error("Failed to send to Zhimo", err);
      alert('发送到智墨失败，请重试');
    } finally {
      setZhimoLoading(false);
    }
  };


  const handleNext = async () => {
    try {
      // Send selected IDs to backend to filter out unselected ones
      // This ensures only the chosen articles are used for generation
      if (selectedItems.length === 0) {
        alert("请至少选择一篇内容");
        return;
      }
      
      setLoading(true);
      await axios.post(`${API_URL}/tasks/${id}/content/filter`, {
        selectedIds: selectedItems
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      navigate(`/tasks/${id}/config`);
    } catch (err) {
      console.error("Failed to save selection", err);
      alert("保存选择失败，请重试");
      setLoading(false);
    }
  };

  // Detail Modal Component
  const DetailModal = () => {
    if (!viewDetailItem) return null;
    const item = viewDetailItem;
    
    // Find index of current item
    const currentIndex = items.findIndex(i => i.id === item.id);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < items.length - 1;

    const goPrev = (e) => {
        e.stopPropagation();
        if (hasPrev) setViewDetailItem(items[currentIndex - 1]);
    };

    const goNext = (e) => {
        e.stopPropagation();
        if (hasNext) setViewDetailItem(items[currentIndex + 1]);
    };

    const tagsList = item.tags 
      ? (typeof item.tags === 'string' ? item.tags.split(',') : (Array.isArray(item.tags) ? item.tags : []))
      : [];
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setViewDetailItem(null)}>
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden border border-gray-200 flex items-center justify-center text-gray-400 font-bold">
                            {item.author?.[0]}
                        </div>
                        <div>
                            <div className="font-bold text-sm text-gray-900">{item.author}</div>
                            <div className="text-xs text-gray-500">{new Date(item.date).toLocaleString()}</div>
                        </div>
                    </div>
                    <button onClick={() => setViewDetailItem(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <span className="text-xl leading-none text-gray-500">&times;</span>
                    </button>
                </div>
                
                {/* Scrollable Body with Nav Arrows */}
                <div className="relative flex-1 flex overflow-hidden">
                    {/* Prev Button */}
                    {hasPrev && (
                        <button 
                            onClick={goPrev}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-blue-600 z-10 transition-all border border-gray-100"
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}
                    
                    {/* Next Button */}
                    {hasNext && (
                        <button 
                            onClick={goNext}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-blue-600 z-10 transition-all border border-gray-100"
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}

                <div className="flex-1 overflow-y-auto p-6 px-14 custom-scrollbar">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 leading-relaxed">{item.title}</h2>
                    
                    {item.coverUrl && (
                        <div className="mb-6 rounded-xl overflow-hidden border border-gray-100 shadow-sm">
                            <img 
                                src={getCompatibleCoverUrl(item.coverUrl)} 
                                className="w-full h-auto object-contain max-h-[400px] bg-gray-50" 
                                referrerPolicy="no-referrer"
                                onError={(e) => e.target.style.display = 'none'}
                            />
                        </div>
                    )}
                    
                    <div className="text-gray-700 text-sm leading-7 whitespace-pre-wrap mb-6 font-normal">
                        {item.content || item.snippet || '暂无内容...'}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                        {tagsList.map((tag, i) => (
                            <span key={i} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">#{tag}</span>
                        ))}
                    </div>
                </div>

                </div>
                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
                    <div className="flex gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Heart size={16} /> {item.likes}</span>
                        <span className="flex items-center gap-1"><Star size={16} /> {item.favs}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={16} /> {item.comments}</span>
                    </div>
                    
                    <div className="flex gap-3">
                         {item.url && (
                            <a 
                                href={item.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                                查看原文 <ArrowRight size={14} />
                            </a>
                        )}
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleItem(item.id);
                                // 移除 setViewDetailItem(null); 保持窗口打开
                            }}
                            className={`px-6 py-2 rounded-lg text-sm font-bold text-white transition-all shadow-md ${selectedItems.includes(item.id) ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                            {selectedItems.includes(item.id) ? '已选定' : '选择此篇'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="text-center">
          <Loader size={32} className="animate-spin text-[#256df3] mx-auto mb-4" />
          <p className="text-gray-500">正在加载抓取结果...</p>
        </div>
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="text-center p-8">
          <p className="text-gray-500 mb-4">未找到相关笔记内容。</p>
          <button 
            onClick={() => navigate('/new-project')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            重新创建任务
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Detail Modal */}
      <DetailModal />

      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between mb-6 sticky top-0 z-30 pt-4 pb-2 bg-gray-50/95 backdrop-blur-sm px-1 gap-4 md:gap-0">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(searchParams.get('from') === 'seed-tasks' ? '/seed-tasks' : '/tasks')} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors group relative flex items-center justify-center text-gray-500 hover:text-gray-900 bg-white shadow-sm border border-gray-200"
            title="返回列表"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
              <span>任务 #{id}</span>
              <span>/</span>
              <span className="text-[#256df3] font-medium">第 2 步：挑选内容 (Live)</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {task?.status === 'GENERATED' ? '素材二次利用' : '第 2 步：挑选内容 (Live)'}
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm w-64">
            <Search size={18} className="text-gray-400" />
            <input type="text" placeholder="筛选标题或标签..." className="bg-transparent border-none outline-none text-sm ml-2 w-full placeholder-gray-400 text-gray-700"/>
          </div>
        </div>
      </header>
                            
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm sticky top-[4.5rem] z-20">
        <div className="flex items-center space-x-4 w-full sm:w-auto mb-3 sm:mb-0">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={toggleAll}>
            <input 
                type="checkbox" 
                className="w-5 h-5 rounded border-gray-300 text-[#256df3] focus:ring-[#256df3]"
                checked={selectedItems.length === items.length} 
                onChange={toggleAll} 
            />
            <span className="text-sm font-medium text-gray-700">全选本页</span>
          </div>
          <div className="px-3 py-1 bg-blue-50 text-[#256df3] rounded-lg text-sm font-semibold">
            已选择 {selectedItems.length} 项
          </div>
        </div>
        <div className="flex space-x-3 w-full sm:w-auto">
          <button className="flex-1 sm:flex-none px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
            重新抓取
          </button>
          
          <button 
            onClick={handleSendToZhimo}
            disabled={zhimoLoading}
            className="flex-1 sm:flex-none px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-200 text-sm font-bold hover:shadow-indigo-300 transition-all transform hover:-translate-y-0.5 flex items-center justify-center space-x-2 mr-3"
          >
            <span>{zhimoLoading ? '处理中...' : '🚀 发往智墨系统'}</span>
          </button>
          <button 
            onClick={handleNext}
    
            className="flex-1 sm:flex-none px-6 py-2 bg-gradient-to-r from-[#256df3] to-[#37a7f3] text-white rounded-xl shadow-lg shadow-blue-200 text-sm font-bold hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
          >
            <span>{task?.status === 'GENERATED' ? '使用选中素材再次生成' : '下一步：本地配置'}</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Grid Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20 overflow-y-auto custom-scrollbar pr-2">
        {items.map(item => (
          <ContentCard 
            key={item.id} 
            item={item} 
            isSelected={selectedItems.includes(item.id)} 
            onToggle={toggleItem} 
            onViewDetail={setViewDetailItem}
            taskStatus={task?.status}
          />
        ))}
      </div>
    </div>
  );
}
